import { CascadeGridPresenter, CascadeMechanic, PreSpinDropOutBoundaryHook, PreSpinDropOutGate, PreSpinDropOutPolicy, buildCascadeReplayPlugin } from "@slot/mechanics-cascade";
import { CompositeGridPresenter } from "@slot/mechanics-core";
import { buildMergeReplayPlugin } from "@slot/mechanics-merge";
import { DelimitedGridCodec, GridLayer, ScalePulseSymbolWinEffect, ScatterHighlightEffect, WinHighlightEffect, createSymbolSpineEffects } from "@slot/presentation";
import { BootGridPresenter, WinSequencePresenter, createShellRoundPlugins } from "@slot/game-shell";
import type { GamePresentation, PresentationDeps } from "@slot/game-shell";
import { cellMultiplierFeature } from "../features/cell-multipliers/CellMultiplierFeature";
import { ChefPresenter } from "../features/chef/ChefPresenter";
import { CellMultiplierView } from "../features/cell-multipliers/CellMultiplierView";
import { secretBonusFeature } from "../features/secret-bonus/SecretBonusFeature";
import { SecretBonusView } from "../features/secret-bonus/SecretBonusView";
import { SECRET_BONUS_HAND_TUNING } from "../tuning/secretBonusTuning";
import type { MergeCandyScene } from "../scene/createScene";
import { GRID_AREA_SIZE, GRID_GEOMETRY } from "../scene/GridContainer";
import { CASCADE_TUNING } from "../tuning/cascadeTuning";
import { CHEF_TUNING } from "../tuning/chefTuning";
import { CELL_MULTIPLIER_BADGE_TUNING, MERGE_TUNING } from "../tuning/mergeTuning";
import { BIG_WIN_TUNING, FEATURE_FINISHED_TUNING, FEATURE_TRIGGER_TUNING } from "../tuning/panelTuning";
import { WIN_PRESENTATION_TUNING } from "../tuning/winPresentationTuning";

/**
 * This game's round presentation (plan §2.2): the grid and its mechanics, the win highlight, the shell's win sequence
 * and the plugin list — everything the shell builds the engine, the runtime and the control bar around. The shared
 * behaviour is the engine's (decision 46); this file names this game's pieces.
 *
 * Mechanic: the merge (`@slot/mechanics-merge`, decision 47) over the cascade (`@slot/mechanics-cascade`). The cascade
 * moves the board — at a spin's start the board clears and the new one drops in, and after a substep the survivors
 * fall while new symbols drop into the gaps; the merge turns a substep's clusters into their outputs before that fall.
 * The cell multiplier badges are this game's feature.
 */
export function createPresentation(scene: MergeCandyScene, deps: PresentationDeps): GamePresentation {
  const { game, gameModals } = scene;
  const s = deps.services;
  const log = s.log;
  const runner = s.presentationTaskRunner;

  // The symbol effects' wiring (decision 46). The title's symbols are sprites: no Spine host, so the dim and the
  // grid's bind and release hooks are what remains of it.
  const symbolEffects = createSymbolSpineEffects({ sources: new Map(), runner, driver: deps.spineDriver, clips: { idle: null, land: null, win: null, out: null } });

  // ── The grid and the board's motion ────────────────────────────────────────────────────────────────────────────
  const gridLayer = new GridLayer(
    {
      gridRoot: game.gridContainer.gridRoot,
      layoutRoot: game.gridContainer.gridArea,
      layoutArea: GRID_AREA_SIZE,
      gridColumnCount: deps.grid.columns,
      gridRowCount: deps.grid.rows,
      cellWidth: GRID_GEOMETRY.pitch,
      cellHeight: GRID_GEOMETRY.pitch,
      autoFitToLayoutRoot: true,
      centerGrid: true,
      columnOffsets: Array.from({ length: deps.grid.columns }, () => 0),
    },
    { definitionResolver: game.symbolCatalog, log, onSymbolBound: symbolEffects.onSymbolBound, onSymbolReleased: symbolEffects.onSymbolReleased },
  );
  const cascade = new CascadeMechanic(gridLayer, { taskRunner: runner, motionTuning: CASCADE_TUNING.motion, isFeatureActive: () => s.featureStateStore.isFeatureActive() });
  // The board clears at a spin's start: the player's spin (`preSpin`) and every free spin's boundary (the hook) share
  // one drop-out, which releases the "spin" barrier as its last symbol starts to fall. A replay has no spin request, so
  // its base spin's boundary drops the board too.
  const dropOutGate = new PreSpinDropOutGate(() => cascade.dropOutDownAndRecycle({ presentationBarrier: s.presentationBarrier }));
  const preSpinBoundaryHook = new PreSpinDropOutBoundaryHook({
    barrier: s.presentationBarrier,
    gate: dropOutGate,
    policy: new PreSpinDropOutPolicy(deps.replayMode),
    presentationTaskRunner: runner,
    presentationClock: s.presentationClock,
    log,
  });
  const basePresenter = new CascadeGridPresenter({
    mechanic: cascade,
    gridCodec: new DelimitedGridCodec(deps.grid.columns, deps.grid.rows),
    presentationBarrier: s.presentationBarrier,
    removalTuning: CASCADE_TUNING.win.removal,
    cascadeTuning: CASCADE_TUNING.motion.cascade,
    log,
  });

  // ── The win presentation ───────────────────────────────────────────────────────────────────────────────────────
  const winHighlightEffect = new WinHighlightEffect({
    gridView: gridLayer,
    log,
    moneyFormatter: s.moneyContext.formatter,
    createWinAmountLabel: game.createWinAmountLabel,
    overlayRoot: game.fxOverlays,
    timings: WIN_PRESENTATION_TUNING.layer,
    labelTuning: WIN_PRESENTATION_TUNING.label,
    symbolWinEffect: new ScalePulseSymbolWinEffect(() => WIN_PRESENTATION_TUNING.scalePulse, runner, { once: true }),
    exposureOwner: symbolEffects.dim,
    taskRunner: runner,
  });
  const scatterHighlightEffect = new ScatterHighlightEffect({ gridView: gridLayer, taskRunner: runner, log });
  const gridPresenter = new CompositeGridPresenter({ base: basePresenter, effects: [winHighlightEffect] });

  // The merge on the cascade's grid: `merge_symbols` empties the consumed cells into their targets and hands them to the
  // cascade's removal, so the collapse event drops into them.
  const mechanic = buildMergeReplayPlugin({
    base: buildCascadeReplayPlugin({ gridPresenter, basePresenter, preSpinBoundaryHook, runner, postRemovalHoldSeconds: CASCADE_TUNING.win.removal.postRemovalHoldSeconds, log }),
    grid: gridLayer,
    gridPresenter,
    runner,
    tuning: MERGE_TUNING,
    log,
  });

  game.boardReadout = () =>
    Array.from({ length: deps.grid.rows }, (_, row) => Array.from({ length: deps.grid.columns }, (_, col) => gridLayer.getSymbolViewAtIndex(gridLayer.indexOf(row, col))?.getSymbolId() ?? ""));

  // The badges belong to the cells: in the grid's space, over the symbols, inside the panel's clip; each colours its
  // cell's tile under the symbols.
  const badgeView = new CellMultiplierView({
    font: scene.assets.fonts.multiplier,
    cellPosition: (index) => gridLayer.getCellLocalPosition(index),
    tileAt: (index) => game.gridContainer.tileAt(index),
    runner,
    tuning: CELL_MULTIPLIER_BADGE_TUNING,
    log,
  });
  game.gridContainer.badgeLayer.addChild(badgeView);
  const cellMultipliers = cellMultiplierFeature.createPresenter({ view: badgeView, log });

  // The Secret Bonus (decision 51): the hand over its cell, above the badges; a tap on the cell opens its unlisted offer.
  const secretBonusView = new SecretBonusView({
    hand: scene.assets.secretBonus.hand,
    cellPosition: (index) => gridLayer.getCellLocalPosition(index),
    ticker: scene.presentationTicker,
    tuning: SECRET_BONUS_HAND_TUNING,
  });
  game.gridContainer.badgeLayer.addChild(secretBonusView);
  const secretBonus = secretBonusFeature.createPresenter({
    view: secretBonusView,
    offers: deps.featureOffers,
    taps: deps.canvasTaps,
    symbolAt: (index) => gridLayer.getSymbolViewAtIndex(index)?.getSymbolId() ?? null,
    log,
  });

  // The chef follows the round: spinning along, cheering a win, celebrating a big win and a free-spins award as their
  // panels enter, greeting a tap on him while the game is idle.
  const chef = new ChefPresenter({
    chef: game.chef,
    spinPhase: s.spinPhaseStore,
    currentWin: s.currentWinStore,
    overlays: s.canvasOverlayStore,
    taps: deps.canvasTaps,
    clock: scene.presentationTicker,
    tuning: CHEF_TUNING,
  });
  chef.start();
  gameModals.bigWinView.onEnter = () => chef.celebrate();
  gameModals.featureTriggerView.onEnter = () => chef.celebrate();

  const bootGridPresenter = new BootGridPresenter({
    grid: {
      reset: () => {
        cellMultipliers.clear();
        secretBonus.clear();
        cascade.resetPresentationState();
      },
      paint: (encoded) => basePresenter.paintGrid(encoded),
    },
    waitForNextRenderedFrame: deps.waitForNextRenderedFrame,
    log,
  });

  // The win sequence (the shell's): the beat before each cluster's win, the cue and haptic, the per-win slam. The title
  // has no win meter: the bar's WIN readout shows the total.
  const winSequencePresenter = new WinSequencePresenter({
    runner,
    audioService: s.audioService,
    hapticsService: s.hapticsService,
    winHighlightEffect,
    tuning: WIN_PRESENTATION_TUNING,
  });

  // ── The replay plugins: the shell's half of the round in its one order, the mechanic in its slot ─────────────────
  const plugins = createShellRoundPlugins({
    services: s,
    lifecycle: deps.lifecycle,
    panels: {
      featureTrigger: gameModals.featureTriggerView,
      featureFinished: gameModals.featureFinishedView,
      bigWin: gameModals.bigWinView,
      gameTransition: gameModals.gameTransitionView,
    },
    tuning: { featureTrigger: FEATURE_TRIGGER_TUNING, featureFinished: FEATURE_FINISHED_TUNING, bigWin: BIG_WIN_TUNING, autoAdvance: WIN_PRESENTATION_TUNING.autoAdvance },
    winSequence: winSequencePresenter,
    scatterHighlightEffect,
    beginSpinCycle: () => gridPresenter.clearForNewSpin(),
    mechanic: mechanic.plugin,
    features: [cellMultiplierFeature.createPlugin(cellMultipliers), secretBonusFeature.createPlugin(secretBonus)],
  });

  return {
    framePresenter: mechanic.framePresenter,
    plugins,
    bootGridPresenter,
    // The player's spin, before the server answers: the last spin's win visuals, badges and Secret Bonus go, and the
    // board clears.
    preSpin: async () => {
      gridPresenter.clearForNewSpin();
      cellMultipliers.clear();
      secretBonus.clear();
      await dropOutGate.request();
    },
    // The board's own motion is the cascade's scopes (the shell slams them); a tap also snaps the win presenting.
    slam: () => winSequencePresenter.slamActiveWin(),
    slamScopes: mechanic.slamScopes,
    gamblePresentation: null,
  };
}
